import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = ({ navigation }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isWhiteBackground, setIsWhiteBackground] = useState(false);
  const [currentSection, setCurrentSection] = useState('hero');

  // Pages that should have black navbar text and borders
  const whiteBackgroundPages = [
    'FeaturesPage',
    'GetStartedSection', 
    'LogoMarquee',
    'TestimonialsPage',
    'ContactFormPage',
    'AboutUsPage'
  ];

  // Check initial hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#about') setIsWhiteBackground(false);
    if (hash === '#privacy') setIsWhiteBackground(true);
    if (hash === '#terms') setIsWhiteBackground(true);
    if (hash === '#security') setIsWhiteBackground(true);
    if (hash === '#docs') setIsWhiteBackground(true);
    if (hash === '#support') setIsWhiteBackground(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // More reliable scroll-based detection
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Force white-background mode (black text) on Privacy, Terms, Security, Docs, Support
      const isPrivacyPage = window.location.hash === '#privacy';
      const isTermsPage = window.location.hash === '#terms';
      const isSecurityPage = window.location.hash === '#security';
      const isDocsPage = window.location.hash === '#docs';
      const isSupportPage = window.location.hash === '#support';
      if (isPrivacyPage || isTermsPage || isSecurityPage || isDocsPage || isSupportPage) {
        setIsWhiteBackground(true);
        setCurrentSection(
          isPrivacyPage ? 'privacy' : isTermsPage ? 'terms' : isSecurityPage ? 'security' : isDocsPage ? 'docs' : 'support'
        );
        return;
      }
      
      // More dynamic approach - use actual element heights when available
      const getSectionHeight = (sectionName) => {
        // Try to get actual element height, fallback to estimated height
        const element = document.querySelector(`[data-section="${sectionName}"]`) || 
                      document.querySelector(`.${sectionName.toLowerCase()}`) ||
                      document.querySelector(`#${sectionName.toLowerCase()}`);
        
        if (element) {
          return element.offsetHeight;
        }
        
        // Fallback to estimated heights based on CSS classes
        const estimatedHeights = {
          hero: windowHeight, // h-screen
          features: windowHeight * 4, // min-h-[400vh]
          products: windowHeight * 2, // min-h-screen + content
          'global-impact': windowHeight * 1.5, // min-h-[150vh]
          'get-started': windowHeight * 4, // min-h-[400vh]
          'logo-marquee': windowHeight * 0.5, // normal height
          testimonials: windowHeight, // min-h-screen
          contact: windowHeight, // min-h-screen
          subscribe: windowHeight * 0.5, // normal height
          footer: windowHeight * 0.5, // normal height
          'our-history': windowHeight * 1, // normal height
          'our-values': windowHeight * 1, // normal height
          'why-choose-us': windowHeight * 3 // min-h-screen + timeline
        };
        
        return estimatedHeights[sectionName] || windowHeight;
      };
      
      // Build sections dynamically - check if we're on About Us page first
      const isAboutUsPage = window.location.hash === '#about' || 
                           document.querySelector('[data-section="our-history"]');
      
      if (isAboutUsPage) {
        let currentPosition = 0;
        const aboutUsSections = [
          { name: 'hero', isWhite: false }, // Dark background
          { name: 'our-history', isWhite: true }, // White background
          { name: 'our-values', isWhite: false }, // Dark background
          { name: 'why-choose-us', isWhite: true } // White background
        ].map(section => {
          const height = getSectionHeight(section.name);
          const start = currentPosition;
          const end = currentPosition + height;
          currentPosition = end;
          
          return {
            name: section.name,
            start,
            end,
            isWhite: section.isWhite
          };
        });
        
        // Find current section
        const currentSection = aboutUsSections.find(section => 
          scrollY >= section.start && scrollY < section.end
        );
        
        if (currentSection) {
          setCurrentSection(currentSection.name);
          setIsWhiteBackground(currentSection.isWhite);
        } else {
          setCurrentSection('unknown');
          setIsWhiteBackground(false);
        }
      } else {
        // Regular main website sections
        let currentPosition = 0;
        const sections = [
          { name: 'hero', isWhite: false },
          { name: 'features', isWhite: true },
          { name: 'products', isWhite: false },
          { name: 'global-impact', isWhite: false },
          { name: 'get-started', isWhite: true },
          { name: 'logo-marquee', isWhite: true },
          { name: 'testimonials', isWhite: true },
          { name: 'contact', isWhite: true },
          { name: 'subscribe', isWhite: false },
          { name: 'footer', isWhite: false }
        ].map(section => {
          const height = getSectionHeight(section.name);
          const start = currentPosition;
          const end = currentPosition + height;
          currentPosition = end;
          
          return {
            name: section.name,
            start,
            end,
            isWhite: section.isWhite
          };
        });
        
        // Find current section
        const currentSection = sections.find(section => 
          scrollY >= section.start && scrollY < section.end
        );
        
        if (currentSection) {
          setCurrentSection(currentSection.name);
          setIsWhiteBackground(currentSection.isWhite);
        } else {
          setCurrentSection('unknown');
          setIsWhiteBackground(false); // Default to dark theme
        }
      }
    };

    // Initial call
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('hashchange', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('hashchange', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`
          fixed top-0 left-0 right-0 z-[999] transition-all duration-300
          ${isScrolled 
            ? 'py-1 lg:py-0.5' 
            : 'py-2 lg:py-1'
          }
          bg-transparent
        `}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          minHeight: 'calc(60px + env(safe-area-inset-top))',
          top: 0,
          left: 0,
          right: 0,
          position: 'fixed'
        }}
      >
        <div className="container-custom px-4 lg:px-8">
          <div className="flex items-center justify-center relative min-h-[60px]">
            {/* Logo - Positioned absolutely to the left */}
            <a href="/" className="absolute left-4 lg:left-0 flex items-center space-x-2">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
                className={`text-sm lg:text-lg font-bold border px-2 lg:px-3 py-1 rounded-2xl transition-colors duration-300 ${
                  isWhiteBackground 
                    ? 'text-gray-900 border-gray-900' 
                    : 'text-white border-gray-600'
                }`}
              >
                FlowMatic™
              </motion.div>
            </a>

            {/* Desktop Navigation - Centered */}
            <div className="hidden lg:flex items-center space-x-2 pt-2">
              <div className={`border px-4 py-1 rounded-2xl transition-colors duration-300 ${
                isWhiteBackground ? 'border-gray-900' : 'border-gray-600'
              }`}>
                <nav className="relative">
                  <ul className="flex items-center space-x-0">
                    {[
                      { name: 'Home', href: '#hero' },
                      { name: 'Products', href: '#products' },
                      { name: 'About Us', href: '#about' }
                    ].map((item, index) => (
                      <motion.li
                        key={item.name}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + index * 0.1 }}
                        className="relative group"
                      >
                        <a
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.name === 'Home') {
                              window.location.hash = '#hero';
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else if (item.name === 'About Us') {
                              // If already on About page, scroll to top; otherwise navigate
                              if (window.location.hash === '#about') {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              } else {
                                window.location.href = '#about';
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            } else {
                              window.location.href = item.href;
                            }
                          }}
                          className={`relative block px-6 py-3 font-medium transition-all duration-300 rounded-lg group-hover:bg-white/10 ${
                            isWhiteBackground 
                              ? 'text-gray-900 hover:text-gray-700' 
                              : 'text-white hover:text-gray-100'
                          }`}
                        >
                          {item.name}
                          {/* Individual sliding indicator for each nav item */}
                          <motion.div
                            className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"
                            initial={{ width: 0 }}
                            whileHover={{ width: "100%" }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          />
                        </a>
                      </motion.li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>

            {/* Desktop Actions - Positioned absolutely to the right */}
            <div className="hidden lg:flex items-center space-x-2 absolute right-0">
              <div className={`border px-4 py-1.5 rounded-2xl flex items-center space-x-3 transition-colors duration-300 ${
                isWhiteBackground ? 'border-gray-900' : 'border-gray-600'
              }`}>
                {/* Animated Tech Breathing Dot */}
                <motion.div
                  className="flex items-center space-x-2"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <motion.div
                    className={`w-2 h-2 rounded-full ${
                      isWhiteBackground ? 'bg-gray-900' : 'bg-green-400'
                    }`}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  <a
                    href="#contact"
                    className={`text-sm font-medium transition-colors duration-200 ${
                      isWhiteBackground 
                        ? 'text-gray-900 hover:text-gray-700' 
                        : 'text-white hover:text-gray-300'
                    }`}
                  >
                    Contact
                  </a>
                </motion.div>
              </div>
            </div>

            {/* Mobile Menu Button - Positioned absolutely to the right */}
            <button
              onClick={toggleMobileMenu}
              className={`lg:hidden absolute right-4 lg:right-0 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-300 ${
                isWhiteBackground 
                  ? 'text-gray-900 hover:bg-gray-100' 
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle mobile menu"
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="w-6 h-6"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </motion.div>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/20" onClick={toggleMobileMenu} />
            <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-xl">
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <div className="text-xl font-bold text-gray-900">Menu</div>
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-lg text-gray-700 hover:bg-gray-100"
                    aria-label="Close menu"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <nav className="space-y-6">
                  {[
                    { name: 'Home', href: '#hero' },
                    { name: 'Products', href: '#products' },
                    { name: 'About Us', href: '#about' }
                  ].map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <a
                        href={item.href}
                        className="block text-lg font-medium text-gray-900 hover:text-gray-700 py-2"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleMobileMenu();
                          if (item.name === 'Home') {
                            window.location.hash = '#hero';
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                          } else if (item.name === 'About Us') {
                            if (window.location.hash === '#about') {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else {
                              window.location.href = '#about';
                              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                            }
                          } else {
                            window.location.href = item.href;
                          }
                        }}
                      >
                        {item.name}
                      </a>
                    </motion.div>
                  ))}
                </nav>

                {/* Contact Us Button */}
                <div className="pt-6 border-t border-gray-200">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <a
                      href="#contact"
                      className="block w-full text-center bg-gray-900 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                      onClick={toggleMobileMenu}
                    >
                      Contact Us
                    </a>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;

